import React from 'react';
import PageLayout from '../components/PageLayout';

const PrivacyPolicy = () => {
  return (
    <PageLayout
      title="Privacy Policy"
      description="How we collect, use, and protect your information"
      variant="default"
      showBookCallButton={false}
    >
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8">
        <div className="prose max-w-none">
          <p className="text-sm text-gray-600 mb-8">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
          </p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">Overview</h2>
              <p className="text-gray-700 leading-relaxed">
                At Terrapin Networks, we are committed to protecting your privacy and ensuring the security 
                of your personal information. This Privacy Policy explains how we collect, use, share, and 
                protect information about you when you visit our website or use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Information You Provide</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Contact information (name, email address, phone number)</li>
                <li>Business information when you inquire about our services</li>
                <li>Communication preferences and meeting scheduling details</li>
                <li>Any other information you choose to provide through our contact forms</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-3">Information Collected Automatically</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Website usage data through Google Analytics (when consented)</li>
                <li>Device and browser information</li>
                <li>IP address and location data</li>
                <li>Pages visited and time spent on our website</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use the information we collect to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Respond to your inquiries and provide customer service</li>
                <li>Schedule consultations and business meetings</li>
                <li>Improve our website and services</li>
                <li>Understand how visitors use our website (with your consent)</li>
                <li>Comply with legal obligations and protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">Cookies and Tracking Technologies</h2>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Cookie Categories</h3>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Necessary Cookies</h4>
                  <p className="text-gray-700 text-sm">
                    Essential for website functionality. These cookies enable basic features like page 
                    navigation, form submissions, and security features. They cannot be disabled.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Analytics Cookies</h4>
                  <p className="text-gray-700 text-sm">
                    Help us understand how visitors interact with our website through Google Analytics. 
                    These cookies collect anonymous information about page views, user interactions, 
                    and website performance. We only use these with your explicit consent.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Functional Cookies</h4>
                  <p className="text-gray-700 text-sm">
                    Enable enhanced functionality and personalization, such as remembering your 
                    preferences and settings for a better user experience.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Marketing Cookies</h4>
                  <p className="text-gray-700 text-sm">
                    Used for social media integration and may track visitors across websites. 
                    We only use these with your explicit consent.
                  </p>
                </div>
              </div>

              <p className="text-gray-700 mt-4">
                You can manage your cookie preferences at any time using our cookie settings, 
                accessible through the link in our website footer.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">How We Share Your Information</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties, 
                except in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>With service providers who assist us in operating our website (e.g., form processing, analytics)</li>
                <li>When required by law or to protect our rights and safety</li>
                <li>With your explicit consent for specific purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">Data Security</h2>
              <p className="text-gray-700">
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction. 
                This includes using secure forms, encryption for data transmission, and regular security 
                assessments of our systems.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">You have the right to:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Withdraw consent for cookie usage at any time</li>
                <li>Opt out of analytics tracking</li>
                <li>Request information about how your data is processed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">Third-Party Services</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Google Analytics</h3>
                  <p className="text-gray-700 text-sm">
                    We use Google Analytics to understand how visitors use our website. Google Analytics 
                    collects anonymous information and creates reports about website activity. We have 
                    configured Google Analytics with privacy-focused settings, including IP anonymization 
                    and disabled advertising features.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Form Processing</h3>
                  <p className="text-gray-700 text-sm">
                    Contact forms on our website are processed through secure third-party services 
                    (Formspree) to ensure reliable delivery and spam protection.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Google Maps</h3>
                  <p className="text-gray-700 text-sm">
                    Our contact page includes an embedded Google Maps component to help visitors 
                    locate our office. This service is provided by Google and subject to their privacy policy.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">Data Retention</h2>
              <p className="text-gray-700">
                We retain personal information only as long as necessary to fulfill the purposes for which 
                it was collected, comply with legal obligations, resolve disputes, and enforce our agreements. 
                Cookie consent preferences are stored for one year, after which you will be asked to 
                provide consent again.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">Children's Privacy</h2>
              <p className="text-gray-700">
                Our website and services are not directed to children under the age of 13. We do not 
                knowingly collect personal information from children under 13. If you believe we have 
                collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time to reflect changes in our practices, 
                technology, or legal requirements. We will notify you of any material changes by posting 
                the updated policy on our website with a new "Last Updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-terrapin-blue mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Terrapin Networks</strong><br />
                  1501 S. Airport Rd West<br />
                  Traverse City, MI 49686<br />
                  Phone: (231) 941-2100<br />
                  Email: info@terrapin.tech
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PrivacyPolicy; 